import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIResource } from "../../constants/application";
import { getUrl } from "utils/getURL";
import axiosInstance from "utils/axiosInstance";
import { PAN_STATUS_VARIANTS } from "constants/panStatusVariants";
import { AppSpecificName } from "constants/headerImage";

interface IValidatePAN {
  data: {
    mobileNumber: string;
    customerId: string;
    panNumber: string;
    establishmentType: string | null;
    customerSource: string;
  };
}

interface IPanResponse {
  panNumber?: string;
  fullName?: string;
  panValidityStatus?: string;
  panOperationalStatus?: string;
  isCompliant?: boolean;
  panAadhaarLinkStatus?: string;
  associatedCustomerDataList?: AssociatedCustomerDataList[];
}

export interface AssociatedCustomerDataList {
  customerId: string;
  shopName: string;
}

interface IPANSlice {
  loading: boolean;
  error: string;
  success: boolean;
  isPanInactive: boolean;
  isInitiallyValidated: boolean;
  isPanVerified: boolean;
  isPanUnavailable: boolean;
  panStatus: (typeof PAN_STATUS_VARIANTS)[keyof typeof PAN_STATUS_VARIANTS];
  fullName: string;
  panResponse: null | IPanResponse;
}

const defaultValues: IPANSlice = {
  loading: false,
  error: "",
  success: false,
  isPanInactive: false,
  isInitiallyValidated: false,
  isPanVerified: false,
  isPanUnavailable: false,
  panStatus: PAN_STATUS_VARIANTS.PENDING,
  fullName: "",
  panResponse: null,
};

// Async thunk for PAN validation
export const ValidatePAN = createAsyncThunk(
  "validatePAN",
  async ({ data }: IValidatePAN, thunkAPI) => {
    const { dispatch } = thunkAPI;
    try {
      const appName:any = localStorage.getItem('appname');
      data['customerSource'] = AppSpecificName[appName];
      const response = await axiosInstance.post(
        getUrl(APIResource.validatePAN, [
          {
            variableName: "customerId",
            variableValue: data.customerId,
          },
        ]),
        {
          ...data,
        }
      );
      if (response.status === 200) {
        if (response.data && data.panNumber) {
          const responseData = response.data;
          if (Object.keys(responseData).length ===0){
             return thunkAPI.rejectWithValue(
              "Something went wrong, Please contact Bayer customer support"
            );
          }

          if (responseData?.message) {
            dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
            dispatch(setPanResponse({ value: null }));
            if (responseData?.error == "KYC-105") {
              return thunkAPI.rejectWithValue(
                "Daily verification limit exceeded. Please try again after 24hrs."
              );
            } else {
              return thunkAPI.rejectWithValue(responseData?.message);
            }
          } else if (!responseData.isCompliant) {
            dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_INACTIVE }));
          } else if (
            String(responseData?.panOperationalStatus).toUpperCase() ===
            "INOPERATIVE"
          ) {
            dispatch(
              setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_INOPERATIVE })
            );
          } else if (responseData && responseData.associatedCustomerDataList && responseData.associatedCustomerDataList.length>0) {
            dispatch(
              setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_ASSOCIATION })
            );
          } else {
            dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_VERIFIED }));
          }
          dispatch(setPanResponse({ value: responseData }));
        } else {
          dispatch(
            setPanStatus({
              value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED,
            })
          );
          dispatch(setPanResponse({ value: null }));
        }
      } else {
        dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
        dispatch(setPanResponse({ value: null }));
        return thunkAPI.rejectWithValue(
          "Error Occurred, Please contact Bayer customer support"
        );
      }
    } catch (error: any) {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
      dispatch(setPanResponse({ value: null }));
      if (error?.response?.data?.message) {
        if (error?.response?.data?.error == "KYC-105") {
          return thunkAPI.rejectWithValue(
            "Daily verification limit exceeded. Please try again after 24hrs."
          );
        } else {
          return thunkAPI.rejectWithValue(error?.response?.data?.message);
        }
      } else {
        return thunkAPI.rejectWithValue(
          "Something went wrong, Please contact Bayer customer support"
        );
      }
    }
  }
);

// Slice definition
const PANSlice = createSlice({
  name: "validatePAN",
  initialState: defaultValues,
  reducers: {
    setPanStatus: (state, action) => {
      state.panStatus = action.payload.value;
    },
    setPanResponse: (state, action) => {
      state.panResponse = action.payload.value;
    },
    setSuccessValue: (state, action) => {
      state.success = action.payload.value;
    },
    setError: (state, action) => {
      state.error = action.payload.value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ValidatePAN.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.success = false;
      })
      .addCase(ValidatePAN.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(ValidatePAN.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { setSuccessValue, setError, setPanStatus, setPanResponse } =
  PANSlice.actions;
export default PANSlice.reducer;
